import React from "react";
import { Stack, StackProps, Text } from "@bookingcom/bui-react";
import { useUnifiedPriceTagController, type UnifiedPriceTagControllerProps } from "./useUnifiedPriceTag.controller";
import { UnifiedPriceTagPriceBreakdown } from "./UnifiedPriceTagPriceBreakdown";
import { UnifiedPriceTagPricePerAdultExplanation } from "./UnifiedPriceTagPricePerAdultExplanation";
import { UnifiedPriceTagExplanation } from "./UnifiedPriceTagExplanation";
import styles from "./UnifiedPriceTag.module.css";
import useRouteName from "../../../hooks/useRouteName";

export type UnifiedPriceTagProps = UnifiedPriceTagControllerProps & Pick<StackProps, "attributes">;

export const UnifiedPriceTagMobile = (props: UnifiedPriceTagProps) => {
  const { formattedPrice, formattedStrikethroughPrice, priceWrapperStackProps, priceStackProps, priceTextProps } =
    useUnifiedPriceTagController(props);
  const { attributes, priceAlignment, priceLabel, strikethroughPriceProps, shouldShowPricePerAdult } = props;
  const routeName = useRouteName();
  const isInFlightDetails = routeName.includes("flightdetails");

  const renderStrikethroughPrice = () => {
    if (!formattedStrikethroughPrice || shouldShowPricePerAdult) return null;
    return (
      <Text
        variant={strikethroughPriceProps?.variant || "body_2"}
        decoration="line-through"
        color="destructive"
        className={isInFlightDetails ? styles.priceInPriceAlignmentExperiment : styles.strikedthroughPrice}
        attributes={{ "data-testid": "upt_price_strikethrough" }}
      >
        {formattedStrikethroughPrice}
      </Text>
    );
  };
  const renderTotalPrice = () => (
    <Text
      className={styles.price}
      {...priceTextProps}
      attributes={{ ...priceTextProps.attributes, "data-testid": "upt_price" }}
    >
      {formattedPrice}
    </Text>
  );
  const renderPriceLabel = () => priceLabel && <Text className={styles.noWrap}>{priceLabel}</Text>;

  if (isInFlightDetails) {
    return (
      <Stack direction="column" gap={0} attributes={{ "data-testid": "upt_container" }}>
        <Stack
          direction="row"
          alignItems="center"
          {...priceStackProps}
          justifyContent={priceAlignment}
          attributes={attributes}
          className={styles.priceContainer}
        >
          {renderStrikethroughPrice()}
          <Stack direction="row" alignItems="center" gap={0.5}>
            {renderPriceLabel()}
            {renderTotalPrice()}
            <UnifiedPriceTagPriceBreakdown {...props} />
          </Stack>
        </Stack>
        <UnifiedPriceTagPricePerAdultExplanation {...props} />
        <UnifiedPriceTagExplanation {...props} />
      </Stack>
    );
  }

  return (
    <Stack direction="column" gap={0} attributes={{ "data-testid": "upt_container" }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={isInFlightDetails ? 0.5 : 1}
        {...priceWrapperStackProps}
        attributes={attributes}
      >
        <Stack {...priceStackProps}>
          {renderStrikethroughPrice()}
          <Stack direction="row" gap={1}>
            {renderPriceLabel()}
            {renderTotalPrice()}
          </Stack>
        </Stack>
        <UnifiedPriceTagPriceBreakdown {...props} />
      </Stack>
      <UnifiedPriceTagPricePerAdultExplanation {...props} />
      <UnifiedPriceTagExplanation {...props} />
    </Stack>
  );
};
